import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Send, Mail } from "lucide-react";

const DEFAULTS = { enabled: false, provider: "resend", resend_api_key: "", smtp_host: "", smtp_port: 587, smtp_username: "",
  smtp_password: "", smtp_encryption: "tls", sender_email: "", sender_name: "Keuangan HN", admin_notify_email: "" };

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

export default function SuperAdminEmail() {
  const [form, setForm] = useState(DEFAULTS);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/security/email/admin").then((r) => setForm((p) => ({ ...p, ...r.data }))).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      const { key, resend_api_key_set, smtp_password_set, ...body } = form; // eslint-disable-line no-unused-vars
      await api.put("/security/email", { ...body, smtp_port: Number(form.smtp_port || 587) });
      toast.success("Pengaturan email disimpan");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const test = async () => {
    if (!testTo) return toast.error("Isi email tujuan uji");
    setTesting(true);
    try { const r = await api.post("/security/email/test", { to: testTo }); toast.success(r.data.message || "Email uji terkirim"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setTesting(false); }
  };
  const isSmtp = form.provider === "smtp";

  return (
    <div>
      <PageHeader title="Pengaturan Email" subtitle="Atur pengiriman email (lupa password, verifikasi pendaftaran, notifikasi, invoice) tanpa mengubah kode."/>
      <div className="p-4 md:p-8 max-w-3xl space-y-6">
        <div className="card-flat p-5 md:p-6 space-y-5" data-testid="email-settings-card">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}><Mail size={18}/></span>
              <div>
                <div className="font-medium text-slate-900">Aktifkan pengiriman email</div>
                <div className="text-xs text-slate-500">Jika nonaktif, email hanya dicatat di log server.</div>
              </div>
            </div>
            <Switch checked={!!form.enabled} onCheckedChange={upd("enabled")} data-testid="email-enabled"/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Penyedia Email">
              <Select value={form.provider} onValueChange={upd("provider")}>
                <SelectTrigger data-testid="email-provider"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resend">Resend (API)</SelectItem>
                  <SelectItem value="smtp">SMTP (email hosting)</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Email Pengirim" hint={isSmtp ? "Biasanya sama dengan username SMTP." : "Domain harus sudah diverifikasi di Resend."}>
              <Input value={form.sender_email} onChange={upd("sender_email")} placeholder="noreply@domainanda.com" data-testid="email-sender"/>
            </F>
            <F label="Nama Pengirim">
              <Input value={form.sender_name} onChange={upd("sender_name")} data-testid="email-sender-name"/>
            </F>
            <F label="Email Notifikasi Pendaftar Baru" hint="Pisahkan dengan koma. Kosong = semua Super Admin.">
              <Input value={form.admin_notify_email} onChange={upd("admin_notify_email")} placeholder="admin@domainanda.com" data-testid="email-admin-notify"/>
            </F>
          </div>
          {isSmtp ? (
            <div className="grid sm:grid-cols-2 gap-4 pt-1" data-testid="email-smtp-fields">
              <F label="Host SMTP"><Input value={form.smtp_host} onChange={upd("smtp_host")} placeholder="mail.domainanda.com" data-testid="email-smtp-host"/></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Port"><Input type="number" value={form.smtp_port} onChange={upd("smtp_port")} data-testid="email-smtp-port"/></F>
                <F label="Enkripsi">
                  <Select value={form.smtp_encryption} onValueChange={upd("smtp_encryption")}>
                    <SelectTrigger data-testid="email-smtp-encryption"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tls">TLS (587)</SelectItem>
                      <SelectItem value="ssl">SSL (465)</SelectItem>
                      <SelectItem value="none">Tanpa</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>
              <F label="Username SMTP"><Input value={form.smtp_username} onChange={upd("smtp_username")} autoComplete="off" data-testid="email-smtp-username"/></F>
              <F label="Password SMTP" hint={form.smtp_password_set ? "Tersimpan. Biarkan ******** bila tidak diubah." : ""}>
                <Input type="password" value={form.smtp_password} onChange={upd("smtp_password")} autoComplete="new-password" data-testid="email-smtp-password"/>
              </F>
            </div>
          ) : (
            <F label="API Key Resend" hint={form.resend_api_key_set ? "Tersimpan. Biarkan ******** bila tidak diubah." : "Buat di resend.com → API Keys."}>
              <Input type="password" value={form.resend_api_key} onChange={upd("resend_api_key")} placeholder="re_xxxxxxxx" autoComplete="new-password" data-testid="email-resend-key"/>
            </F>
          )}
          <Button onClick={save} disabled={saving} className="brand-btn rounded-sm" data-testid="email-save-btn"><Save size={14} className="mr-1.5"/>{saving ? "Menyimpan..." : "Simpan Pengaturan"}</Button>
        </div>
        <div className="card-flat p-5 md:p-6 space-y-3" data-testid="email-test-card">
          <div className="font-medium text-slate-900">Kirim Email Uji</div>
          <p className="text-xs text-slate-500">Simpan pengaturan dulu, lalu kirim email uji untuk memastikan konfigurasi benar.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email tujuan" data-testid="email-test-to"/>
            <Button onClick={test} disabled={testing} variant="outline" className="rounded-sm shrink-0" data-testid="email-test-btn"><Send size={14} className="mr-1.5"/>{testing ? "Mengirim..." : "Kirim Uji"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
